import React, { Component } from 'react';
import { postPotentialSupplierRegistrationForm } from '../../../apis/forms';
import { getCountries, getStatesByCountryId } from '../../../apis/locale';
import toast from '../../../components/toast';
import animatedScrollTo from 'animated-scroll-to';
import { yearsInBusiness, yesNo, currencies, diversity, commodityClassifications } from '../../../helpers/shared-form-data.json';
import kebabCase from 'lodash/kebabCase';
import BaseSelect from 'react-select';
import RequiredSelect from './required-select.jsx';
import { addParamsToUrl } from '../../../utils/';
import ReCAPTCHA from "react-google-recaptcha";

const Select = props => (
    <RequiredSelect
        {...props}
        SelectComponent={BaseSelect}
        options={props.options || options}
    />
);

const initialState = {
    company: '',
    country: 'US',
    state: 'AL',
    city: '',
    addressLine1: '',
    addressLine2: '',
    postalCode: '',
    websiteURL: '',
    salesPersonName: '',
    salesPersonPhone: '',
    salesPersonEmail: '',
    ISOCertified: '',
    yearsInBusiness: '',
    dbNumber: '',
    annualRevenue: '',
    annualRevenueCurrency: currencies[0],
    employeeSize: '',
    supplierDiversity: '',
    countryLocations: '',
    commodityClassification: '',
    companyClassification: '',
    apartFromCompetition: '',
    recaptchaToken: ''
};

class PotentialSupplierRegistration extends Component {
    constructor(props) {
        super(props);

        this.state = Object.assign({}, initialState, {
            isSending: false,
            countries: [],
            states: [],
            countryOptions: [],
            diversityOptions: diversity.map(d => {
                return { label: d, value: d };
            }),
            classificationOptions: commodityClassifications.map(c => {
                return { label: c.name, value: c.value };
            }),
            selectedCountries: [],
            selectedDiversity: [],
            selectedClassifications: [],
            errors: []
        });

        this.handleChange = this.handleChange.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleDiversityChange = this.handleDiversityChange.bind(this);
        this.handleCountryServiceChange = this.handleCountryServiceChange.bind(this);
        this.handleClassificationChange = this.handleClassificationChange.bind(this);

        this.form = React.createRef();
        this.recaptchaRef = React.createRef();
    }

    componentDidMount() {
        this.loadCountries();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.country !== this.state.country && this.state.country) {
            this.loadStates();
        }
    }

    loadCountries() {
        getCountries()
            .then(data => {
                this.setState({
                    countries: data,
                    countryOptions: data.map(c => {
                        return {
                            label: c.countryDisplayName,
                            value: c.countryDisplayName
                        };
                    })
                }, () => {
                    if(this.state.country) this.loadStates();
                });
            });
    }

    loadStates() {
        this.setState({
            states: []
        });
        const country = this.state.countries.find(c => c.countryTwoLetterCode === this.state.country);
        getStatesByCountryId(country.countryID)
            .then(data => {
                this.setState({
                    states: data
                });
            });
    }

    handleFormSubmit(e) {
        e.preventDefault();

        if (!this.state.isSending) {
            this.setState({
                isSending: true,
                errors: [],
                recaptchaToken: this.recaptchaRef.current.getValue()
            }, () => {

            // send form to api
            postPotentialSupplierRegistrationForm(Object.assign({}, this.state))
                .then(json => {
                    console.log(json);
                    this.setState({
                        isSending: false
                    });

                    if (json.success) {
                        toast.success('Your registration has been submitted!');
                        if (this.props.redirectTo) window.location = this.props.redirectTo;
                        else {
                            this.form.current.reset();
                            this.setState(Object.assign({}, initialState));
                            this.setState({
                                selectedCountries: [],
                                selectedDiversity: [],
                                selectedClassifications: []
                            });
                        }
                    }
                    else {
                        this.setState({
                            errors: json.errors
                        });
                        animatedScrollTo(document.querySelector('#carrier-registration-form'));
                    }
                })
                .catch(error => {
                    this.setState({
                        isSending: false
                    });
                    console.log(error);
                    toast.error('There was an issue sending your registration. Please try again soon.');
                });
            });
        }
    }

    handleChange(e) {
        const target = event.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const name = target.name;

        this.setState({
            [name]: value,
            errors: []
        });
    }

    handleDiversityChange(selectedDiversity) {
        this.setState({
            selectedDiversity,
            supplierDiversity: selectedDiversity.map(s => s.label).join(', ')
        });
    }

    handleCountryServiceChange(selectedCountries) {
        this.setState({
            selectedCountries,
            countryLocations: selectedCountries.map(s => s.label).join(', ')
        });
    }

    handleClassificationChange(selectedClassifications) {
        this.setState({
            selectedClassifications,
            commodityClassification: selectedClassifications.map(s => s.value).join(', ')
        });
    }

    render() {
        let states,
            errors;
        if (this.state.states.length) {
            states = <div className="form-group col-lg-2 col-md-6">
                <label htmlFor="state">State *</label>
                <select className="form-control" id="state" name="state" required value={this.state.state} onChange={this.handleChange}>
                    {this.state.states.map((state) => (
                        <option value={state.stateCode}>{state.stateCode}</option>
                    ))}
                </select>
            </div>;
        }
        if (this.state.errors.length) {
            errors = <ul className="error-list">
                {this.state.errors.map((error) => (
                    <li>{error}</li>    
                ))}
            </ul>;
        }

        return (
            <form id="carrier-registration-form" action="#" method="post" onSubmit={this.handleFormSubmit} ref={this.form}>
                {errors}
                <div className="row">
                    <div className="form-group col-md-12">
                        <label htmlFor="company">Company Name *</label>
                        <input className="form-control" type="text" id="company" name="company" placeholder="Acme Studios" required value={this.state.company} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="streetAddress">Street Address *</label>
                        <input className="form-control" type="text" id="streetAddress" name="addressLine1" placeholder="123 East St." required value={this.state.addressLine1} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="addressLine2">Address Line 2</label>
                        <input className="form-control" type="text" id="addressLine2" name="addressLine2" placeholder="Ste. 5" value={this.state.addressLine2} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-lg-4 col-md-6">
                        <label htmlFor="city">City *</label>
                        <input className="form-control" type="text" id="city" name="city" placeholder="Sheboygan" required="" value={this.state.city} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-lg-4 col-md-6">
                        <label htmlFor="country">Country *</label>
                        <select className="form-control" id="country" name="country" required value={this.state.country} onChange={this.handleChange}>
                            {this.state.countries.map((country) => (
                                <option value={country.countryTwoLetterCode}>{country.countryDisplayName}</option>    
                            ))}
                        </select>
                    </div>
                    {states}
                    <div className="form-group col-lg-2 col-md-6">
                        <label htmlFor="postalCode">Postal Code *</label>
                        <input className="form-control" type="text" id="postalCode" name="postalCode" placeholder="12345" required="" value={this.state.postalCode} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="websiteURL">Website URL</label>
                        <input className="form-control" type="url" id="websiteURL" name="websiteURL" placeholder="https://mywebsite.com" value={this.state.websiteURL} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="salesPersonName">Sales Person Name *</label>
                        <input className="form-control" type="text" id="salesPersonName" name="salesPersonName" placeholder="John Doe" required required value={this.state.salesPersonName} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-md-6">
                        <label htmlFor="salesPersonEmail">Sales Person Email *</label>
                        <input className="form-control" type="email" id="salesPersonEmail" name="salesPersonEmail" placeholder="johndoe@gmail.com" required value={this.state.salesPersonEmail} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-md-6">
                        <label htmlFor="salesPersonPhone">Sales Person Phone *</label>
                        <input className="form-control" type="tel" id="salesPersonPhone" name="salesPersonPhone" placeholder="123-123-1234" required value={this.state.salesPersonPhone} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-md-12">
                        <span className="label">Is your company ISO certified? *</span>
                        <ul className="radio-list">
                            {yesNo.map((item) => (
                                <li>
                                    <input className="custom-radio" id={`ISOCertified${kebabCase(item)}`} name="ISOCertified" type="radio" required value={item} onChange={this.handleChange} />
                                    <label htmlFor={`ISOCertified${kebabCase(item)}`}>{item}</label>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="yearsInBusiness">Years In Business *</label>
                        <select className="form-control" required id="yearsInBusiness" name="yearsInBusiness" value={this.state.yearsInBusiness} onChange={this.handleChange}>
                            {yearsInBusiness.map((item) => (
                                <option value={item}>{item}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="dbNumber">Dun & Bradstreet Number * <span style={{ color: 'red' }}>(Type N/A if not applicable)</span></label>
                        <input className="form-control" type="text" id="dbNumber" name="dbNumber" placeholder="" required value={this.state.dbNumber} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="annualRevenue">Annual Revenue (Numbers only) *</label>
                        <input className="form-control" type="number" id="annualRevenue" name="annualRevenue" placeholder="" required value={this.state.annualRevenue} onChange={this.handleChange} />
                        <select className="form-control" id="annualRevenueCurrency" name="annualRevenueCurrency" value={this.state.annualRevenueCurrency} onChange={this.handleChange}>
                            {currencies.map((item) => (
                                <option value={item}>{item}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="employeeSize">Employee Size *</label>
                        <select className="form-control" required id="employeeSize" name="employeeSize" value={this.state.employeeSize} onChange={this.handleChange}>
                            <option value="< 25">&lt; 25</option>
                            <option value="26 - 100">26 - 100</option>
                            <option value="101 - 250">101 - 250</option>
                            <option value="251 - 750">251 - 750</option>
                            <option value="751 - 1500">751 - 1500</option>
                            <option value="1501 - 5000">1501 - 5000</option>
                            <option value="> 5000">&gt; 5000</option>
                        </select>
                    </div>
                    <div className="form-group col-md-12">
                        <label>Supplier Diversity (Select all that apply) *</label>
                        <Select
                            isMulti
                            value={this.state.selectedDiversity}
                            onChange={this.handleDiversityChange}
                            options={this.state.diversityOptions}
                            required
                        />
                    </div>
                    <div className="form-group col-md-12">
                        <label>Select the countries your company has locations in (Select all that apply) *</label>
                        <Select
                            isMulti
                            value={this.state.selectedCountries}
                            onChange={this.handleCountryServiceChange}
                            options={this.state.countryOptions}
                            required
                        />
                    </div>
                    <div className="form-group col-md-12">
                        <label>Commodity Classification (Select all that apply) *</label>
                        <Select
                            isMulti
                            value={this.state.selectedClassifications}
                            onChange={this.handleClassificationChange}
                            options={this.state.classificationOptions}
                            required
                        />
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="companyClassification">Elaborate further on your company's commodity classification. *</label>
                        <textarea className="form-control" id="companyClassification" name="companyClassification" placeholder="" rows="3" required value={this.state.companyClassification} onChange={this.handleChange} />
                    </div>
                    <div className="form-group col-md-12">
                        <label htmlFor="apartFromCompetition">What abilities does your company have that sets you apart from your competition? </label>
                        <textarea className="form-control" id="apartFromCompetition" name="apartFromCompetition" placeholder="" rows="3" value={this.state.apartFromCompetition} onChange={this.handleChange} />
                    </div>
                </div>
                <ReCAPTCHA
                    sitekey="6LcOndkZAAAAAE7Hb968THW1Zyjt3MGCcF4R2NHQ"
                    ref={this.recaptchaRef}
                />
                <div className="form-actions">
                    <button className="btn" disabled={this.state.isSending}>Send</button>
                </div>
            </form>
        );
    }
}

export default PotentialSupplierRegistration;